import type { LandingData } from "@/components/landing/LandingTemplate";

/**
 * Catálogo de landings SEO de LevelLocal.
 * Cada entrada produce una ruta /<slug> con SEO único, contenido propio
 * y enlaces internos hacia landings relacionadas.
 */

const COMMON_FEATURES_FULL = [
  "Gestión de la ficha de Google Maps desde un único panel",
  "Solicitud de reseñas reales a clientes de forma ordenada",
  "Borradores de respuesta con IA y revisión humana antes de publicar",
  "Alertas inmediatas ante reseñas negativas o de baja puntuación",
  "Publicaciones, fotos, horarios y servicios actualizados",
  "Informes de llamadas, visitas a la web e indicaciones",
  "Comparativa de rendimiento entre ubicaciones",
  "Soporte en español y onboarding asistido",
];

const SECTOR_LINKS = [
  { to: "/google-maps-restaurantes", label: "Google Maps para restaurantes" },
  { to: "/google-maps-clinicas", label: "Google Maps para clínicas" },
  { to: "/google-maps-hoteles", label: "Google Maps para hoteles" },
  { to: "/google-maps-inmobiliarias", label: "Google Maps para inmobiliarias" },
  { to: "/google-maps-talleres", label: "Google Maps para talleres" },
  { to: "/google-maps-centros-estetica", label: "Google Maps para centros de estética" },
  { to: "/google-maps-sector-lujo", label: "Reputación en Google Maps para marcas premium" },
];

const SUPPORT_LINKS = [
  // Movidos a /blog/<slug> (posts editoriales en lugar de landings).
  { to: "/blog/mejorar-posicionamiento-google-maps", label: "Mejorar posicionamiento en Google Maps" },
  { to: "/blog/conseguir-resenas-google", label: "Conseguir más reseñas en Google" },
  { to: "/blog/mejorar-resenas-google", label: "Mejorar reseñas de Google" },
  { to: "/blog/responder-resenas-google", label: "Responder reseñas de Google" },
  { to: "/blog/resenas-negativas-google", label: "Gestionar reseñas negativas en Google" },
  { to: "/blog/seo-local-google-maps", label: "SEO local en Google Maps" },
];

const ENTERPRISE_LINKS = [
  { to: "/agencias-seo-local", label: "Herramienta SEO local para agencias" },
  { to: "/varias-ubicaciones-google-maps", label: "Gestionar varias fichas de Google Maps" },
];

function pickLinks(slugs: string[]) {
  const all = [...SECTOR_LINKS, ...SUPPORT_LINKS, ...ENTERPRISE_LINKS];
  return slugs
    .map((s) => {
      // Permite pasar slugs sin prefijo (legacy: "conseguir-resenas-google")
      // y los resuelve tanto a landings como a posts del blog.
      return (
        all.find((l) => l.to === `/${s}`) ??
        all.find((l) => l.to === `/blog/${s}`)
      );
    })
    .filter((x): x is { to: string; label: string } => Boolean(x));
}

/* ============================================================
   1. SECTORIALES
   ============================================================ */

const restaurantes: LandingData = {
  slug: "google-maps-restaurantes",
  seo: {
    title: "Google Maps para restaurantes | LevelLocal",
    description:
      "Haz que tu restaurante destaque en Google Maps, mejore sus reseñas y convierta más búsquedas locales en reservas y clientes.",
  },
  hero: {
    eyebrow: "Restaurantes",
    h1: "Haz que tu restaurante destaque en Google Maps",
    subtitle:
      "LevelLocal ayuda a restaurantes, bares y grupos de restauración a mejorar su visibilidad en Google Maps, gestionar reseñas, responder opiniones y conseguir más reservas desde búsquedas locales.",
    ctaPrimary: "Solicitar demo para restaurantes",
    ctaSecondary: "Ver planes",
  },
  problem: {
    title: "La mayoría de tus clientes elige restaurante mirando Google Maps",
    paragraphs: [
      "Antes de reservar mesa, los clientes abren Google Maps, comparan estrellas, leen reseñas, miran fotos del local y revisan el menú. En segundos deciden a qué restaurante llamar y a cuál no.",
      "Si tu ficha está incompleta, tienes pocas reseñas, no respondes a las opiniones o aparecen comentarios negativos sin contestar, pierdes reservas que se van directamente a la competencia.",
      "El problema no es la calidad de tu cocina: es que no estás trabajando bien el escaparate digital donde la gente decide.",
    ],
  },
  solution: {
    title: "Cómo te ayuda LevelLocal",
    paragraphs: [
      "LevelLocal centraliza la gestión de tu ficha de Google Maps: reseñas, respuestas, publicaciones, fotos, horarios y métricas, todo desde un único panel pensado para hostelería.",
      "Te ayuda a pedir reseñas reales a clientes satisfechos, a responder de forma profesional con la ayuda de IA (siempre con revisión humana) y a recibir alertas inmediatas cuando llega una opinión negativa.",
      "Además, mantienes la ficha viva con publicaciones de menús, ofertas, eventos y novedades, y mides cuántas llamadas, visitas a la web y solicitudes de cómo llegar genera tu restaurante.",
    ],
  },
  benefits: {
    title: "Lo que consigues con LevelLocal en tu restaurante",
    items: [
      { title: "Más reseñas reales", desc: "Procesos sencillos para que tus clientes felices dejen su valoración tras la visita." },
      { title: "Mejor reputación", desc: "Respuestas profesionales a cada reseña, positiva o negativa, sin descuidar el tono de la marca." },
      { title: "Alertas negativas", desc: "Te avisamos al instante de cualquier reseña baja para que puedas reaccionar antes de que escale." },
      { title: "Ficha siempre viva", desc: "Publicaciones de menús, ofertas y eventos que mejoran tu visibilidad en Google Maps." },
      { title: "Más reservas", desc: "Convierte búsquedas locales en llamadas, visitas a la web y reservas directas." },
      { title: "Multi-restaurante", desc: "Gestiona uno o varios locales desde un único panel, con informes por ubicación." },
    ],
  },
  features: {
    title: "Funcionalidades pensadas para restaurantes",
    items: COMMON_FEATURES_FULL,
  },
  useCases: {
    title: "Para restaurantes individuales y grupos de restauración",
    paragraphs: [
      "LevelLocal funciona igual de bien para un restaurante familiar que quiere mejorar su valoración media en su barrio, que para un grupo de restauración que necesita controlar la reputación de varios locales y unificar el tono de las respuestas.",
      "Si gestionas varias marcas o ubicaciones, podrás ver de un vistazo qué local está rindiendo mejor, dónde están entrando reseñas negativas y qué acciones están generando más reservas.",
    ],
  },
  stats: {
    title: "Por qué Google Maps decide tus reservas",
    intro: "Datos públicos del sector restauración y búsqueda local que explican el impacto real de las reseñas en tus reservas.",
    items: [
      { figure: "94%", label: "de los comensales evita un restaurante por reseñas negativas en internet.", source: "Estudio TripAdvisor / Phocuswright, 2023" },
      { figure: "+0,1★", label: "de subida de valoración media se traduce en hasta un 25% más de ingresos por restaurante.", source: "Harvard Business School (Michael Luca), Yelp Study" },
      { figure: "76%", label: "de las búsquedas locales en móvil terminan en una visita al negocio en menos de 24 horas.", source: "Think with Google, Local Search Behavior" },
    ],
  },
  faq: {
    items: [
      { q: "¿Cómo puede ayudar Google Maps a conseguir más reservas?", a: "Una ficha completa, con buenas fotos, horarios actualizados, publicaciones recientes y reseñas bien valoradas y respondidas, transmite confianza y convierte muchas más búsquedas en llamadas y reservas directas." },
      { q: "¿Por qué son importantes las reseñas de Google en un restaurante?", a: "Las reseñas son el principal factor de decisión para elegir restaurante. Una valoración alta, sumada a respuestas profesionales, hace que el cliente te elija frente a otros locales con peor reputación." },
      { q: "¿LevelLocal responde automáticamente a las reseñas?", a: "Generamos un borrador de respuesta con IA adaptado al tono de tu marca, pero siempre puedes revisarlo y editarlo antes de publicarlo. Nunca publicamos sin tu visto bueno si así lo configuras." },
      { q: "¿Puedo revisar las respuestas antes de publicarlas?", a: "Sí. Puedes activar el modo de revisión obligatoria para que ninguna respuesta salga publicada sin que tú o tu equipo la apruebe." },
      { q: "¿Puedo gestionar varios restaurantes desde una sola cuenta?", a: "Sí, LevelLocal está pensado también para grupos de restauración: gestiona varias fichas, compara su rendimiento y unifica el estilo de las respuestas." },
      { q: "¿Puedo publicar menús, ofertas o eventos en mi ficha de Google?", a: "Sí, puedes crear publicaciones de Google con menús especiales, promociones, eventos o novedades, lo que ayuda a mantener la ficha activa y mejorar la visibilidad." },
      { q: "¿Cuánto tarda un restaurante en mejorar su valoración media en Google?", a: "Con un proceso ordenado de solicitud de reseñas y respuesta profesional, la mayoría de restaurantes nota una mejora visible de su valoración media entre la 4ª y la 8ª semana. La velocidad depende del volumen de clientes y de la valoración de partida." },
      { q: "¿Es legal pedir reseñas a los clientes de mi restaurante?", a: "Sí, es legal y está permitido por Google siempre que pidas la reseña a clientes reales, no ofrezcas incentivos económicos a cambio y no filtres por valoración. LevelLocal está diseñado para cumplir las políticas de Google." },
      { q: "¿Cómo eliminar una reseña falsa o injusta en Google?", a: "No se pueden borrar reseñas reales aunque sean negativas, pero sí se pueden reportar a Google las que incumplen sus políticas (spam, conflictos de interés, contenido ofensivo, competidores). Desde LevelLocal te ayudamos a detectarlas, redactar la reclamación y hacer el seguimiento." },
    ],
  },
  internalLinks: {
    title: "Sigue explorando",
    links: pickLinks([
      "mejorar-posicionamiento-google-maps",
      "conseguir-resenas-google",
      "responder-resenas-google",
      "resenas-negativas-google",
      "varias-ubicaciones-google-maps",
    ]),
  },
  ctaFinal: {
    title: "Haz que tu restaurante destaque en Google Maps",
    subtitle: "Te enseñamos en una demo de 30 minutos cómo mejorar reseñas, atraer reservas y mantener tu ficha siempre actualizada.",
    cta: "Solicitar demo para restaurantes",
  },
};

const clinicas: LandingData = {
  slug: "google-maps-clinicas",
  seo: {
    title: "Google Maps para clínicas | LevelLocal",
    description:
      "Mejora la visibilidad y reputación de tu clínica en Google Maps, gestiona reseñas de pacientes y consigue más solicitudes de cita.",
  },
  hero: {
    eyebrow: "Clínicas",
    h1: "Mejora la visibilidad y reputación de tu clínica en Google Maps",
    subtitle:
      "LevelLocal ayuda a clínicas médicas, dentales, estéticas y sanitarias a cuidar su reputación en Google, responder reseñas y convertir búsquedas locales en solicitudes de cita.",
    ctaPrimary: "Solicitar demo para clínicas",
    ctaSecondary: "Ver planes",
  },
  problem: {
    title: "Los pacientes te buscan en Google antes de pedir cita",
    paragraphs: [
      "Cuando alguien necesita un dentista, un fisioterapeuta o una clínica estética, lo primero que hace es buscar en Google Maps las opciones cercanas y leer las reseñas de otros pacientes.",
      "Una reputación con pocas estrellas, reseñas negativas sin responder o información desactualizada genera dudas y hace que el paciente acabe pidiendo cita en otra clínica.",
      "Cuidar tu presencia en Google Maps no es opcional: es donde se gana o se pierde la confianza del paciente antes incluso de hablar contigo.",
    ],
  },
  solution: {
    title: "Cómo te ayuda LevelLocal",
    paragraphs: [
      "LevelLocal te ayuda a mantener una ficha de Google Maps cuidada, profesional y actualizada, y a gestionar las reseñas de tus pacientes con un tono adecuado al sector sanitario.",
      "Recibes alertas inmediatas cuando llega una reseña negativa para que puedas reaccionar a tiempo, y dispones de borradores de respuesta con IA que tu equipo puede revisar y aprobar.",
      "Además, ves en informes claros cuántas llamadas, solicitudes de indicaciones y visitas a la web está generando cada clínica, para entender qué está funcionando y qué no.",
    ],
  },
  benefits: {
    title: "Lo que consigues con LevelLocal en tu clínica",
    items: [
      { title: "Más confianza", desc: "Reseñas bien gestionadas y respuestas profesionales que transmiten seriedad sanitaria." },
      { title: "Reputación protegida", desc: "Detectamos reseñas negativas a tiempo para que tu equipo pueda responder con criterio." },
      { title: "Más solicitudes de cita", desc: "Convierte búsquedas locales en llamadas, formularios y solicitudes de indicaciones." },
      { title: "Tono profesional", desc: "Borradores de respuesta con IA adaptados al lenguaje del sector salud." },
      { title: "Informes claros", desc: "Métricas de captación local por clínica y por canal." },
      { title: "Multi-sede", desc: "Gestiona varias clínicas o gabinetes desde un solo panel." },
    ],
  },
  features: { title: "Funcionalidades pensadas para clínicas", items: COMMON_FEATURES_FULL },
  stats: {
    title: "Lo que dicen los pacientes sobre las reseñas online",
    intro: "Datos públicos sobre cómo los pacientes eligen clínica antes de pedir cita.",
    items: [
      { figure: "84%", label: "de los pacientes confía en las reseñas online tanto como en una recomendación personal.", source: "BrightLocal — Local Consumer Review Survey, 2024" },
      { figure: "75%", label: "de los pacientes usa Google como primer paso para buscar un nuevo profesional sanitario.", source: "Software Advice — Patient Reviews Survey" },
      { figure: "4,0★", label: "es la valoración mínima que el 70% de los pacientes considera aceptable para pedir cita.", source: "BrightLocal Healthcare Review Study" },
    ],
  },
  faq: {
    items: [
      { q: "¿Por qué son importantes las reseñas para una clínica?", a: "Porque la decisión de pedir cita se basa en confianza. Una clínica con buenas reseñas y respuestas profesionales transmite seguridad y atrae más pacientes que otra con peor reputación." },
      { q: "¿Cómo puede una clínica mejorar su reputación en Google?", a: "Pidiendo reseñas reales a pacientes satisfechos, respondiendo a todas las opiniones con criterio profesional, manteniendo la ficha actualizada y reaccionando rápido a las reseñas negativas." },
      { q: "¿Puedo recibir alertas si llega una reseña negativa?", a: "Sí, LevelLocal te avisa al instante por email para que puedas responder antes de que la reseña se viralice o impacte en tu reputación." },
      { q: "¿La IA publica respuestas automáticamente?", a: "No por defecto. La IA propone borradores y tu equipo decide si se publican tal cual, se editan o se descartan. Mantienes el control en todo momento." },
      { q: "¿Sirve para clínicas dentales, médicas o estéticas?", a: "Sí, trabajamos con todo tipo de clínicas: dentales, médicas, estéticas, fisioterapia, podología, oftalmología, etc." },
      { q: "¿Puedo gestionar varias sedes?", a: "Sí, puedes gestionar varias clínicas desde un único panel, con informes individuales y comparativas entre sedes." },
      { q: "¿Cuánto tarda una clínica en mejorar su valoración en Google?", a: "Con un proceso ordenado de solicitud de reseñas a pacientes satisfechos y respuesta profesional, la mayoría de clínicas ven mejoras notables entre la 6ª y la 10ª semana. Cuanto mayor sea el volumen de pacientes, antes se nota." },
      { q: "¿Es legal pedir reseñas a los pacientes y se vulnera la confidencialidad?", a: "Es legal pedir reseñas a los pacientes siempre que ellos decidan libremente si dejarla y qué contar. LevelLocal nunca expone datos clínicos: solo facilita el enlace para que el paciente, si quiere, valore la atención recibida en Google." },
      { q: "¿Cómo eliminar una reseña falsa de un paciente que nunca ha venido a la clínica?", a: "No se puede borrar directamente, pero sí reportar a Google si vulnera sus políticas (paciente inexistente, competencia, contenido injurioso). En LevelLocal te ayudamos a detectarla, redactar la reclamación y hacer el seguimiento hasta su resolución." },
    ],
  },
  internalLinks: {
    title: "Sigue explorando",
    links: pickLinks([
      "mejorar-posicionamiento-google-maps",
      "mejorar-resenas-google",
      "resenas-negativas-google",
      "varias-ubicaciones-google-maps",
    ]),
  },
  ctaFinal: {
    title: "Mejora la presencia de tu clínica en Google Maps",
    subtitle: "Te mostramos en una demo cómo cuidar tu reputación, responder mejor a tus pacientes y captar más solicitudes de cita.",
    cta: "Solicitar demo para clínicas",
  },
};

const hoteles: LandingData = {
  slug: "google-maps-hoteles",
  seo: {
    title: "Google Maps para hoteles | LevelLocal",
    description:
      "Controla la reputación de tu hotel en Google Maps, responde reseñas y mejora la confianza de los viajeros antes de reservar.",
  },
  hero: {
    eyebrow: "Hoteles",
    h1: "Controla la reputación de tu hotel en Google Maps",
    subtitle:
      "LevelLocal ayuda a hoteles, apartamentos turísticos y alojamientos boutique a gestionar reseñas, responder opiniones y mejorar su presencia local en Google Maps.",
    ctaPrimary: "Solicitar demo para hoteles",
    ctaSecondary: "Ver planes",
  },
  problem: {
    title: "El viajero compara hoteles antes de reservar",
    paragraphs: [
      "Antes de reservar, el viajero busca en Google Maps, lee reseñas de huéspedes anteriores, mira fotos y revisa cómo responde el hotel a las opiniones, sobre todo a las negativas.",
      "Un par de reseñas mal gestionadas pueden costarte reservas durante meses, y un perfil con poca actividad transmite que el hotel no presta atención a sus huéspedes.",
      "Cuidar Google Maps es cuidar la primera impresión que tendrá tu próximo huésped.",
    ],
  },
  solution: {
    title: "Cómo te ayuda LevelLocal",
    paragraphs: [
      "LevelLocal centraliza la gestión de las reseñas de Google de tu hotel, te ayuda a responder con un tono profesional y cuidado, y te alerta de cualquier opinión negativa para que puedas reaccionar a tiempo.",
      "Mantienes la ficha activa con publicaciones de novedades, servicios, eventos y promociones, y dispones de informes pensados para dirección, no solo para marketing.",
    ],
  },
  benefits: {
    title: "Lo que consigues con LevelLocal en tu hotel",
    items: [
      { title: "Reputación bajo control", desc: "Sigue todas las reseñas de Google desde un único panel." },
      { title: "Respuestas profesionales", desc: "Borradores con IA adaptados al tono del sector hotelero." },
      { title: "Alertas negativas", desc: "Te avisamos al instante de cada reseña baja para gestionar la crisis." },
      { title: "Ficha siempre activa", desc: "Publicaciones de servicios, novedades y experiencias del hotel." },
      { title: "Confianza del viajero", desc: "Más reservas directas gracias a una mejor reputación digital." },
      { title: "Informes para dirección", desc: "Métricas claras y comparativas entre alojamientos." },
    ],
  },
  features: { title: "Funcionalidades pensadas para hoteles", items: COMMON_FEATURES_FULL },
  stats: {
    title: "Cómo influyen las reseñas en la decisión del viajero",
    intro: "Datos públicos del sector hotelero y turismo sobre el peso real de las reseñas en la reserva.",
    items: [
      { figure: "81%", label: "de los viajeros lee siempre o frecuentemente reseñas antes de reservar alojamiento.", source: "TripAdvisor — Global Travel Insights, 2023" },
      { figure: "+1★", label: "de subida de valoración permite a un hotel subir el precio medio hasta un 11% sin perder ocupación.", source: "Cornell School of Hotel Administration" },
      { figure: "87%", label: "de los viajeros considera importante que el hotel responda a las reseñas, sobre todo a las negativas.", source: "TripAdvisor TripBarometer" },
    ],
  },
  faq: {
    items: [
      { q: "¿Sirve LevelLocal para hoteles independientes?", a: "Sí, está pensado tanto para hoteles independientes como para grupos hoteleros y apartamentos turísticos." },
      { q: "¿Ayuda a responder reseñas de huéspedes?", a: "Sí, generamos borradores de respuesta con IA adaptados al tono de hospitalidad, y tu equipo los revisa antes de publicar." },
      { q: "¿Puedo gestionar varios alojamientos?", a: "Sí, puedes administrar varios hoteles o apartamentos desde un único panel, con informes y permisos por ubicación." },
      { q: "¿Cómo ayuda a mejorar la reputación del hotel?", a: "Combinando solicitud de reseñas reales, respuestas profesionales, alertas negativas y publicaciones que mantengan la ficha viva." },
      { q: "¿Puedo publicar novedades o servicios en Google?", a: "Sí, puedes crear publicaciones con servicios, ofertas, eventos o novedades de tu alojamiento." },
      { q: "¿Cuánto tarda un hotel en mejorar su valoración media en Google?", a: "Con una rutina constante de solicitud de reseñas al check-out y respuesta profesional a todas las opiniones, la valoración media suele moverse al alza entre la 6ª y la 12ª semana. Hoteles con alta ocupación ven los efectos antes." },
      { q: "¿Es legal pedir reseñas a los huéspedes después de su estancia?", a: "Sí, siempre que la reseña sea voluntaria, sin incentivos económicos y sin filtrar por puntuación. LevelLocal está diseñado para cumplir las políticas de Google y enviar la solicitud justo después del check-out." },
      { q: "¿Cómo gestionar una reseña negativa injusta de un huésped en Google?", a: "Primero respondemos públicamente con tono profesional para mostrar al resto de viajeros cómo actúa el hotel. Si la reseña incumple las políticas de Google (insultos, competencia, datos falsos), te ayudamos a reportarla y hacer seguimiento hasta su retirada." },
    ],
  },
  internalLinks: {
    title: "Sigue explorando",
    links: pickLinks([
      "mejorar-resenas-google",
      "responder-resenas-google",
      "resenas-negativas-google",
      "google-maps-sector-lujo",
    ]),
  },
  ctaFinal: {
    title: "Cuida la reputación de tu hotel en Google Maps",
    subtitle: "En una demo de 30 minutos te enseñamos cómo gestionar reseñas, alertas y publicaciones desde un único panel.",
    cta: "Solicitar demo para hoteles",
  },
};

const inmobiliarias: LandingData = {
  slug: "google-maps-inmobiliarias",
  seo: {
    title: "Google Maps para inmobiliarias | LevelLocal",
    description:
      "Mejora la reputación de tu inmobiliaria en Google Maps, gestiona reseñas y genera más confianza en propietarios, compradores y vendedores.",
  },
  hero: {
    eyebrow: "Inmobiliarias",
    h1: "Haz que tu inmobiliaria genere más confianza en Google Maps",
    subtitle:
      "LevelLocal ayuda a inmobiliarias y agencias de real estate a mejorar su visibilidad local, gestionar reseñas y convertir búsquedas en contactos de propietarios y compradores.",
    ctaPrimary: "Solicitar demo para inmobiliarias",
    ctaSecondary: "Ver planes",
  },
  problem: {
    title: "La confianza decide a qué inmobiliaria llama el cliente",
    paragraphs: [
      "Vender o alquilar una vivienda es una decisión de mucho dinero. Antes de llamar, propietarios y compradores buscan en Google la inmobiliaria, leen reseñas y comparan reputaciones.",
      "Una agencia con pocas reseñas, opiniones negativas sin responder o ficha incompleta pierde captaciones y clientes ante competidores que sí cuidan su reputación digital.",
    ],
  },
  solution: {
    title: "Cómo te ayuda LevelLocal",
    paragraphs: [
      "LevelLocal te ayuda a pedir reseñas a propietarios, compradores e inquilinos satisfechos, a responder con un tono profesional y a detectar a tiempo las reseñas negativas o delicadas.",
      "Mides cuántas llamadas, visitas web y solicitudes de ubicación está generando tu oficina en Google Maps y comparas el rendimiento entre varias oficinas si las tienes.",
    ],
  },
  benefits: {
    title: "Lo que consigues con LevelLocal en tu inmobiliaria",
    items: [
      { title: "Más confianza", desc: "Reseñas reales y respuestas cuidadas que generan credibilidad." },
      { title: "Más contactos", desc: "Convierte búsquedas locales en llamadas y formularios." },
      { title: "Reputación protegida", desc: "Alertas inmediatas ante reseñas negativas o delicadas." },
      { title: "Tono profesional", desc: "Borradores de respuesta adaptados al sector inmobiliario." },
      { title: "Informes claros", desc: "Métricas de captación local por oficina." },
      { title: "Multi-oficina", desc: "Gestiona varias oficinas desde un único panel." },
    ],
  },
  features: { title: "Funcionalidades pensadas para inmobiliarias", items: COMMON_FEATURES_FULL },
  faq: {
    items: [
      { q: "¿Por qué son importantes las reseñas para una inmobiliaria?", a: "Porque vender o alquilar una vivienda es una decisión basada en confianza. Las reseñas son la primera prueba social que el cliente revisa antes de llamarte." },
      { q: "¿Puede LevelLocal ayudarme a conseguir más contactos?", a: "Sí, mejorando tu reputación en Google Maps y manteniendo activa tu ficha conseguirás más llamadas, formularios y solicitudes de ubicación desde búsquedas locales." },
      { q: "¿Puedo responder reseñas con ayuda de IA?", a: "Sí, generamos borradores de respuesta que tu equipo puede revisar, editar y aprobar antes de publicar." },
      { q: "¿Sirve para inmobiliarias de lujo?", a: "Sí, adaptamos el tono y el enfoque a marcas premium para no perder elegancia ni control de marca." },
      { q: "¿Puedo gestionar varias oficinas?", a: "Sí, desde un solo panel puedes gestionar varias oficinas con informes individuales y comparativas." },
    ],
  },
  internalLinks: {
    title: "Sigue explorando",
    links: pickLinks([
      "mejorar-posicionamiento-google-maps",
      "conseguir-resenas-google",
      "google-maps-sector-lujo",
      "varias-ubicaciones-google-maps",
    ]),
  },
  ctaFinal: {
    title: "Genera más confianza en Google Maps",
    subtitle: "Te mostramos en una demo cómo gestionar reseñas y captar más propietarios y compradores desde Google Maps.",
    cta: "Solicitar demo para inmobiliarias",
  },
};

const talleres: LandingData = {
  slug: "google-maps-talleres",
  seo: {
    title: "Google Maps para talleres | LevelLocal",
    description:
      "Consigue más llamadas para tu taller desde Google Maps, mejora tus reseñas y genera más confianza antes de que el cliente te contacte.",
  },
  hero: {
    eyebrow: "Talleres",
    h1: "Consigue más llamadas para tu taller desde Google Maps",
    subtitle:
      "LevelLocal ayuda a talleres mecánicos, carrocerías y negocios de reparación a mejorar su presencia en Google Maps, gestionar reseñas y atraer más clientes locales.",
    ctaPrimary: "Solicitar demo para talleres",
    ctaSecondary: "Ver planes",
  },
  problem: {
    title: "El cliente busca taller cerca y elige por reseñas",
    paragraphs: [
      "Cuando el coche falla, el cliente abre Google Maps, busca \"taller cerca de mí\" y elige al primero que tenga buenas estrellas, fotos del local y respuestas profesionales.",
      "Si tu taller no aparece bien posicionado, no tiene reseñas o muestra opiniones negativas sin responder, esa llamada se va directamente al taller de al lado.",
    ],
  },
  solution: {
    title: "Cómo te ayuda LevelLocal",
    paragraphs: [
      "LevelLocal te ayuda a pedir reseñas reales a tus clientes después del servicio, a responderlas con un tono profesional y a detectar a tiempo las reseñas negativas para gestionarlas antes de que escalen.",
      "Y, sobre todo, ves cuántas llamadas e indicaciones está generando tu ficha de Google Maps cada semana.",
    ],
  },
  benefits: {
    title: "Lo que consigues con LevelLocal en tu taller",
    items: [
      { title: "Más llamadas", desc: "Convierte búsquedas locales en llamadas reales al taller." },
      { title: "Mejor reputación", desc: "Más reseñas reales y mejores valoraciones." },
      { title: "Alertas negativas", desc: "Te avisamos para que puedas responder a tiempo." },
      { title: "Ficha viva", desc: "Publicaciones, fotos y servicios actualizados." },
      { title: "Informes claros", desc: "Llamadas, indicaciones y visitas web por taller." },
      { title: "Multi-taller", desc: "Gestiona varios talleres desde un único panel." },
    ],
  },
  features: { title: "Funcionalidades pensadas para talleres", items: COMMON_FEATURES_FULL },
  faq: {
    items: [
      { q: "¿Cómo puede un taller conseguir más clientes desde Google Maps?", a: "Apareciendo bien en las búsquedas locales con una ficha completa, buenas reseñas, respuestas profesionales y fotos reales del taller y del equipo." },
      { q: "¿Por qué son importantes las reseñas para un taller?", a: "Porque la mecánica genera desconfianza por defecto. Buenas reseñas y respuestas claras reducen el miedo y hacen que el cliente te llame antes que a otro." },
      { q: "¿Puedo responder reseñas negativas?", a: "Sí, te ayudamos con borradores con tono profesional para gestionar quejas sin escalar el conflicto." },
      { q: "¿Puedo saber cuántas llamadas llegan desde Google?", a: "Sí, puedes ver el número de llamadas, solicitudes de indicaciones y visitas a la web generadas por tu ficha." },
      { q: "¿Sirve para varios talleres?", a: "Sí, puedes gestionar varios talleres con informes individuales por ubicación." },
    ],
  },
  internalLinks: {
    title: "Sigue explorando",
    links: pickLinks(["conseguir-resenas-google", "mejorar-resenas-google", "seo-local-google-maps"]),
  },
  ctaFinal: {
    title: "Más llamadas reales desde Google Maps",
    subtitle: "Te enseñamos en una demo de 30 minutos cómo trabajar tu ficha y conseguir más clientes locales.",
    cta: "Solicitar demo para talleres",
  },
};

const estetica: LandingData = {
  slug: "google-maps-centros-estetica",
  seo: {
    title: "Google Maps para centros de estética | LevelLocal",
    description:
      "Mejora la visibilidad de tu centro de estética en Google Maps, consigue más reseñas y convierte búsquedas locales en citas.",
  },
  hero: {
    eyebrow: "Centros de estética",
    h1: "Haz que tu centro de estética destaque en Google Maps",
    subtitle:
      "LevelLocal ayuda a centros de estética, belleza y bienestar a gestionar reseñas, mejorar su reputación local y conseguir más citas desde Google Maps.",
    ctaPrimary: "Solicitar demo para centros de estética",
    ctaSecondary: "Ver planes",
  },
  problem: {
    title: "La confianza visual decide la cita",
    paragraphs: [
      "Las clientas eligen centro de estética mirando reseñas, fotos del local, fotos de tratamientos y opiniones de otras clientas. Si tu ficha está vacía o poco cuidada, pierdes citas frente a centros con mejor escaparate digital.",
      "Y una sola reseña negativa sin responder puede frenar muchas reservas si no se gestiona a tiempo.",
    ],
  },
  solution: {
    title: "Cómo te ayuda LevelLocal",
    paragraphs: [
      "LevelLocal te ayuda a mantener una ficha cuidada con fotos, servicios, horarios y publicaciones de promociones, tratamientos y novedades.",
      "Te facilita pedir reseñas a tus clientas satisfechas, responderlas con un tono cuidado y recibir alertas inmediatas ante reseñas negativas.",
    ],
  },
  benefits: {
    title: "Lo que consigues con LevelLocal en tu centro de estética",
    items: [
      { title: "Más reseñas reales", desc: "Solicitud ordenada a clientas tras la visita." },
      { title: "Más citas", desc: "Una mejor reputación se traduce en más reservas." },
      { title: "Reputación protegida", desc: "Alertas instantáneas ante opiniones negativas." },
      { title: "Promociones visibles", desc: "Publica tratamientos, ofertas y novedades en tu ficha." },
      { title: "Tono cuidado", desc: "Borradores de respuesta acordes a tu marca." },
      { title: "Multi-centro", desc: "Gestiona varios centros desde un único panel." },
    ],
  },
  features: { title: "Funcionalidades pensadas para centros de estética", items: COMMON_FEATURES_FULL },
  faq: {
    items: [
      { q: "¿Cómo puede un centro de estética conseguir más citas desde Google?", a: "Mejorando su reputación, manteniendo la ficha activa con fotos y publicaciones, y respondiendo a todas las reseñas." },
      { q: "¿Puedo publicar promociones o tratamientos?", a: "Sí, puedes publicar promociones, tratamientos nuevos o eventos directamente desde el panel." },
      { q: "¿Puedo recibir alertas si llega una reseña negativa?", a: "Sí, te avisamos al instante para que puedas responder antes de que dañe tu reputación." },
      { q: "¿Puedo gestionar varios centros?", a: "Sí, puedes gestionar varios centros con informes individuales y comparativas." },
      { q: "¿Las respuestas con IA se publican solas?", a: "No por defecto. Generamos borradores que tu equipo aprueba antes de publicar." },
    ],
  },
  internalLinks: {
    title: "Sigue explorando",
    links: pickLinks(["conseguir-resenas-google", "mejorar-posicionamiento-google-maps", "mejorar-resenas-google"]),
  },
  ctaFinal: {
    title: "Convierte búsquedas locales en citas",
    subtitle: "Te mostramos en una demo cómo trabajar tu ficha y mejorar tu reputación local.",
    cta: "Solicitar demo para centros de estética",
  },
};

const lujo: LandingData = {
  slug: "google-maps-sector-lujo",
  seo: {
    title: "LevelLocal Premium · Reputación digital para marcas de lujo",
    description:
      "Gestión de reputación digital premium para restaurantes de lujo, hoteles boutique, beach clubs, clínicas estéticas y real estate exclusivo. Tu Google alineado con tu marca.",
  },
  hero: {
    eyebrow: "LevelLocal Premium",
    h1: "Reputación digital premium para marcas de lujo",
    subtitle:
      "Instagram genera deseo, pero Google valida confianza. Cuidamos la presencia en Google de restaurantes premium, hoteles boutique, beach clubs, clínicas estéticas y real estate de lujo, alineada con el nivel de experiencia que ofreces.",
    ctaPrimary: "Solicitar propuesta Premium",
  },
  problem: {
    title: "Lo que detectamos en marcas premium",
    paragraphs: [
      "Hoy incluso el cliente de alto poder adquisitivo valida digitalmente una marca antes de reservar, visitar o contactar. Y la ficha de Google se ha convertido en una extensión directa de tu identidad.",
      "Habitualmente vemos reseñas sin responder o respondidas con plantillas genéricas, fotografías que no transmiten exclusividad, falta de coherencia entre Instagram y Google, publicaciones abandonadas y, sobre todo, ausencia de estrategia reputacional y visual.",
      "El resultado: clientes potenciales que validan online y no encuentran una imagen a la altura de la experiencia real del negocio.",
    ],
  },
  solution: {
    title: "Qué hacemos desde LevelLocal Premium",
    paragraphs: [
      "Gestión avanzada de reputación digital con respuestas premium personalizadas mediante IA, supervisión específica de reseñas delicadas o sensibles y un flujo de aprobación humano obligatorio antes de publicar nada.",
      "Diseñamos una estrategia visual para Google Maps con publicaciones optimizadas que refuerzan la imagen de marca, hacemos seguimiento de la percepción y reputación emocional, activamos alertas reputacionales inmediatas y comparamos tu marca frente a la competencia premium.",
      "Tu equipo decide qué se publica, cómo y cuándo. Los informes están pensados para dirección y para marca, no solo para marketing.",
    ],
  },
  benefits: {
    title: "Cómo trabajamos las reseñas en lujo",
    items: [
      { title: "Activación discreta", desc: "Sin automatizaciones agresivas ni peticiones invasivas: facilitamos, no presionamos." },
      { title: "Selección inteligente", desc: "Identificamos al cliente y el momento adecuado para invitarle a dejar reseña." },
      { title: "Timing adecuado", desc: "La solicitud se realiza cuando la experiencia está fresca y la percepción es máxima." },
      { title: "Comunicación elegante", desc: "Tono y vocabulario coherentes con la voz de tu marca, sin plantillas." },
      { title: "Experiencia primero", desc: "La automatización trabaja en segundo plano; la marca siempre se percibe humana." },
      { title: "Aprobación humana", desc: "Nada se publica sin que tu equipo lo revise y lo valide." },
    ],
  },
  features: {
    title: "Servicio LevelLocal Premium",
    items: [
      "Gestión avanzada de reputación digital",
      "Respuestas premium personalizadas con IA y supervisión humana",
      "Supervisión específica de reseñas delicadas o sensibles",
      "Estrategia visual para Google Maps coherente con la marca",
      "Publicaciones optimizadas para reforzar imagen y posicionamiento",
      "Seguimiento de percepción y reputación emocional",
      "Alertas reputacionales inmediatas",
      "Análisis comparativo frente a competencia premium",
      "Coherencia entre Instagram y Google",
      "Aprobación humana obligatoria antes de publicar",
    ],
  },
  useCases: {
    title: "Sectores ideales para LevelLocal Premium",
    paragraphs: [
      "Trabajamos con restaurantes premium, beach clubs, hoteles boutique, clínicas estéticas, real estate de lujo, estudios de interiorismo y arquitectura, y marcas de wellness y experiencias exclusivas.",
      "Objetivo del servicio: mejorar la percepción premium del negocio, incrementar la confianza antes de la reserva, generar coherencia entre redes sociales y Google, aumentar reservas, llamadas y visitas, y convertir Google Maps en una herramienta real de branding.",
    ],
  },
  faq: {
    items: [
      { q: "¿Tiene sentido pedir reseñas en el sector lujo?", a: "Sí, pero con discreción. No usamos automatizaciones agresivas: aplicamos activación discreta, selección inteligente del cliente y timing adecuado para que la solicitud sea elegante y coherente con la marca." },
      { q: "¿Cómo se gestionan reseñas sin dañar la percepción de marca?", a: "Con respuestas premium personalizadas mediante IA, supervisión específica de reseñas delicadas o sensibles y siempre con revisión humana antes de publicar. Nada de plantillas genéricas." },
      { q: "¿Por qué cuidar Google si nuestra marca vive en Instagram?", a: "Porque Instagram genera deseo, pero Google valida confianza. Antes de reservar, visitar o contactar, el cliente premium revisa tu ficha. Si no está a la altura, se rompe la coherencia de marca." },
      { q: "¿Trabajáis con beach clubs, hoteles boutique y clínicas estéticas?", a: "Sí, también con restaurantes premium, real estate de lujo, interiorismo y arquitectura, y marcas de wellness y experiencias exclusivas." },
      { q: "¿Qué incluye la estrategia visual para Google Maps?", a: "Publicaciones optimizadas para reforzar la imagen de marca, coherencia visual entre Instagram y Google y un seguimiento de percepción y reputación emocional." },
      { q: "¿Tenemos control sobre lo que se publica?", a: "Sí. Aprobación humana obligatoria: tu equipo decide qué se publica, cómo y cuándo. Nada sale sin revisión." },
      { q: "¿Cómo medís el impacto?", a: "Con seguimiento de percepción y reputación emocional, alertas reputacionales inmediatas e informes comparativos frente a competencia premium." },
    ],
  },
  internalLinks: {
    title: "Sigue explorando",
    links: pickLinks([
      "google-maps-restaurantes",
      "google-maps-hoteles",
      "google-maps-inmobiliarias",
      "resenas-negativas-google",
    ]),
  },
  ctaFinal: {
    title: "Que tu Google esté a la altura de tu marca",
    subtitle: "La reputación digital ya forma parte de la experiencia de marca. Te preparamos una propuesta LevelLocal Premium adaptada al posicionamiento y al nivel de experiencia que ofreces a tus clientes.",
    cta: "Solicitar propuesta Premium",
  },
};

/* ============================================================
   2. CIUDADES
   ============================================================ */

function cityLanding(opts: {
  slug: string;
  city: string;
  cityIntro: string;
  cityContext: string;
}): LandingData {
  return {
    slug: opts.slug,
    seo: {
      title: `Posicionamiento en Google Maps en ${opts.city} | LevelLocal`,
      description: `Ayudamos a negocios de ${opts.city} a mejorar su presencia en Google Maps, conseguir más reseñas y convertir búsquedas locales en llamadas y clientes.`,
    },
    hero: {
      eyebrow: opts.city,
      h1: `Mejora la visibilidad de tu negocio en Google Maps en ${opts.city}`,
      subtitle: `LevelLocal ayuda a restaurantes, clínicas, hoteles, inmobiliarias, talleres y comercios de ${opts.city} a destacar en Google Maps y atraer más clientes locales.`,
      ctaPrimary: `Solicitar demo en ${opts.city}`,
      ctaSecondary: "Ver planes",
    },
    problem: {
      title: `Competir por clientes en ${opts.city} pasa por Google Maps`,
      paragraphs: [
        opts.cityIntro,
        opts.cityContext,
        `Si tu negocio no está bien posicionado en Google Maps en ${opts.city}, estás regalando llamadas, reservas y visitas a la competencia local que sí cuida su ficha.`,
      ],
    },
    solution: {
      title: "Cómo te ayuda LevelLocal",
      paragraphs: [
        `LevelLocal centraliza la gestión de tu ficha de Google Maps: reseñas, respuestas, fotos, horarios, publicaciones y métricas, todo pensado para que tu negocio en ${opts.city} destaque frente a la competencia local.`,
        "Te ayuda a pedir reseñas reales a tus clientes, a responder con un tono profesional con la ayuda de IA (siempre con revisión humana) y a recibir alertas inmediatas ante cualquier opinión negativa.",
      ],
    },
    benefits: {
      title: `Lo que consigues con LevelLocal en ${opts.city}`,
      items: [
        { title: "Más visibilidad", desc: `Mejor presencia en búsquedas locales en ${opts.city}.` },
        { title: "Más reseñas reales", desc: "Solicitud ordenada a clientes satisfechos." },
        { title: "Mejor reputación", desc: "Respuestas profesionales a cada opinión." },
        { title: "Alertas negativas", desc: "Te avisamos al instante para reaccionar a tiempo." },
        { title: "Más llamadas y visitas", desc: "Convierte búsquedas locales en clientes reales." },
        { title: "Multi-ubicación", desc: "Gestiona uno o varios locales desde un único panel." },
      ],
    },
    features: { title: "Funcionalidades pensadas para tu negocio local", items: COMMON_FEATURES_FULL },
    faq: {
      items: [
        { q: `¿LevelLocal funciona para negocios pequeños en ${opts.city}?`, a: "Sí, está pensado tanto para negocios independientes como para grupos con varias ubicaciones." },
        { q: `¿En qué tipos de negocio en ${opts.city} encaja mejor?`, a: "Restaurantes, clínicas, hoteles, inmobiliarias, talleres, centros de estética y comercios locales." },
        { q: "¿Cuánto tarda en notarse la mejora?", a: "Las primeras mejoras de actividad se notan en semanas; el impacto real en visibilidad y reseñas se consolida en pocos meses." },
        { q: "¿Puedo gestionar varias ubicaciones?", a: "Sí, desde un único panel con informes individuales por ubicación." },
        { q: "¿La IA publica respuestas automáticamente?", a: "No por defecto. Generamos borradores y tu equipo aprueba antes de publicar." },
      ],
    },
    internalLinks: {
      title: "Sigue explorando",
      links: pickLinks([
        "google-maps-restaurantes",
        "google-maps-clinicas",
        "google-maps-hoteles",
        "google-maps-inmobiliarias",
        "mejorar-posicionamiento-google-maps",
      ]),
    },
    ctaFinal: {
      title: `Más clientes locales en ${opts.city}`,
      subtitle: `Te enseñamos en una demo cómo trabajar tu Google Maps para captar más clientes en ${opts.city}.`,
      cta: `Solicitar demo en ${opts.city}`,
    },
  };
}

const malaga = cityLanding({
  slug: "google-maps-malaga",
  city: "Málaga",
  cityIntro:
    "Málaga es un mercado muy competitivo: la oferta de restaurantes, clínicas, hoteles, inmobiliarias y comercios crece cada año y el cliente local —y también el turista— elige a través de Google Maps.",
  cityContext:
    "Aparecer bien posicionado en Google Maps en Málaga influye directamente en llamadas, reservas y visitas a la web. Reseñas, fotos, publicaciones, horarios y respuestas marcan la diferencia frente a competidores que descuidan su ficha.",
});

const sevilla = cityLanding({
  slug: "google-maps-sevilla",
  city: "Sevilla",
  cityIntro:
    "Sevilla concentra una alta densidad de negocios locales en hostelería, salud, comercio y servicios profesionales que compiten por aparecer en las mismas búsquedas en Google Maps.",
  cityContext:
    "El cliente sevillano abre Google Maps, compara reseñas y reputación y elige al negocio que mejor transmite confianza. Una ficha cuidada y reseñas bien gestionadas son la diferencia entre captar o perder clientes locales.",
});

const cadiz = cityLanding({
  slug: "google-maps-cadiz",
  city: "Cádiz",
  cityIntro:
    "Cádiz combina cliente local y cliente turístico, lo que multiplica el peso de Google Maps: el visitante que llega busca dónde comer, alojarse o ir antes incluso de aterrizar.",
  cityContext:
    "Para hostelería, hoteles, comercios y servicios, una buena reputación en Google Maps en Cádiz es fundamental: marca la diferencia entre llenar sala, ocupar habitaciones o quedarte fuera de la decisión.",
});

const valladolid = cityLanding({
  slug: "google-maps-valladolid",
  city: "Valladolid",
  cityIntro:
    "Valladolid tiene un tejido sólido de negocios locales —clínicas, restaurantes, comercios, talleres y servicios— donde la cercanía y la confianza son clave para captar clientes.",
  cityContext:
    "Aparecer bien en Google Maps en Valladolid es la forma más rentable de atraer clientes locales recurrentes y nuevas oportunidades sin depender solo del boca a boca.",
});

const granada = cityLanding({
  slug: "google-maps-granada",
  city: "Granada",
  cityIntro:
    "Granada combina un fuerte tejido de hostelería, comercio y servicios con un flujo constante de turistas y estudiantes que deciden a través de Google Maps dónde comer, alojarse o reservar.",
  cityContext:
    "Posicionarse bien en Google Maps en Granada —con reseñas cuidadas, fotos actualizadas y respuestas profesionales— es lo que separa a los negocios que llenan cada día del resto.",
});

/* ============================================================
   3. APOYO SEO GENERAL
   ============================================================ */

const mejorarPosicionamiento: LandingData = {
  slug: "mejorar-posicionamiento-google-maps",
  seo: {
    title: "Mejorar posicionamiento en Google Maps | LevelLocal",
    description:
      "Aprende cómo mejorar tu posicionamiento en Google Maps trabajando reseñas, actividad, fotos, publicaciones y respuestas. Plataforma todo-en-uno.",
  },
  hero: {
    eyebrow: "SEO local",
    h1: "Mejora el posicionamiento de tu negocio en Google Maps",
    subtitle:
      "Trabaja todos los factores que influyen en tu visibilidad local: reseñas, actividad, fotos, publicaciones, horarios, servicios, respuestas e informes, desde una única plataforma.",
    ctaPrimary: "Solicitar demo",
    ctaSecondary: "Ver planes",
  },
  problem: {
    title: "No basta con tener ficha de Google",
    paragraphs: [
      "Tener creada la ficha de Google Maps no es suficiente. Google premia a los negocios que mantienen actividad: nuevas reseñas, respuestas, publicaciones, fotos, horarios actualizados y servicios bien definidos.",
      "Si tu ficha está parada, Google la muestra menos. Si no respondes a las reseñas, pierdes confianza. Y si no publicas, otros negocios que sí lo hacen ocupan tu sitio en los resultados locales.",
    ],
  },
  solution: {
    title: "Cómo te ayuda LevelLocal",
    paragraphs: [
      "LevelLocal trabaja en paralelo todos los factores que mejoran tu posicionamiento en Google Maps: reseñas reales, respuestas profesionales, publicaciones recurrentes, alertas y métricas claras.",
      "Es la forma más eficiente de mantener tu ficha viva sin tener que dedicarle horas todas las semanas.",
    ],
  },
  benefits: {
    title: "Factores que vamos a trabajar",
    items: [
      { title: "Reseñas reales", desc: "Más volumen y mejor valoración media." },
      { title: "Respuestas", desc: "Atención profesional a cada reseña." },
      { title: "Publicaciones", desc: "Ficha viva con novedades, ofertas y eventos." },
      { title: "Fotos y servicios", desc: "Información completa y actualizada." },
      { title: "Horarios", desc: "Coherencia entre Google y la realidad." },
      { title: "Informes", desc: "Llamadas, indicaciones y visitas web." },
    ],
  },
  features: { title: "Todo lo que incluye la plataforma", items: COMMON_FEATURES_FULL },
  faq: {
    items: [
      { q: "¿Garantizan la primera posición en Google Maps?", a: "No. Nadie puede garantizarlo de forma honesta. Lo que hacemos es trabajar todos los factores que Google sí valora para que tu ficha mejore de forma sostenida." },
      { q: "¿En cuánto tiempo se notan los resultados?", a: "Las primeras mejoras de actividad se ven en semanas. La mejora de visibilidad y reseñas se consolida en unos meses." },
      { q: "¿Sirve para cualquier sector?", a: "Sí, especialmente para negocios locales con clientes cercanos: hostelería, salud, comercios, servicios profesionales, talleres, etc." },
      { q: "¿Tengo que tener conocimientos técnicos?", a: "No. La plataforma está pensada para empresarios y equipos no técnicos." },
      { q: "¿Puedo gestionar varias ubicaciones?", a: "Sí, con informes y permisos por ubicación." },
    ],
  },
  internalLinks: {
    title: "Sigue explorando",
    links: pickLinks([
      "conseguir-resenas-google",
      "responder-resenas-google",
      "resenas-negativas-google",
      "seo-local-google-maps",
      "varias-ubicaciones-google-maps",
    ]),
  },
  ctaFinal: {
    title: "Mejora tu posicionamiento en Google Maps",
    subtitle: "Te enseñamos en una demo cómo trabajar todos los factores que influyen en tu visibilidad local.",
    cta: "Solicitar demo",
  },
};

const conseguirResenas: LandingData = {
  slug: "conseguir-resenas-google",
  seo: {
    title: "Conseguir más reseñas en Google | LevelLocal",
    description:
      "Consigue más reseñas reales de tus clientes en Google de forma ordenada y profesional. Sin trampas ni reseñas falsas.",
  },
  hero: {
    eyebrow: "Reseñas",
    h1: "Consigue más reseñas reales de tus clientes en Google",
    subtitle:
      "LevelLocal te ayuda a solicitar reseñas a tus clientes satisfechos de forma ordenada y profesional, sin tácticas agresivas y sin reseñas falsas.",
    ctaPrimary: "Solicitar demo",
    ctaSecondary: "Ver planes",
  },
  problem: {
    title: "Tus clientes felices no escriben reseñas por defecto",
    paragraphs: [
      "La mayoría de tus clientes contentos no dejan reseña a no ser que se lo pidas: el cliente enfadado escribe siempre, el cliente feliz casi nunca.",
      "Si no organizas el flujo de solicitud, tu valoración media se queda baja y refleja peor el servicio real que prestas.",
    ],
  },
  solution: {
    title: "Cómo te ayuda LevelLocal",
    paragraphs: [
      "LevelLocal te facilita pedir reseñas reales a tus clientes en el momento adecuado, con mensajes profesionales y enlaces directos.",
      "Nada de comprar reseñas ni de generarlas artificialmente: solo opiniones reales de clientes reales.",
    ],
  },
  benefits: {
    title: "Por qué importa tener más reseñas",
    items: [
      { title: "Más confianza", desc: "Cuantas más reseñas reales, más creíble es tu negocio." },
      { title: "Mejor valoración media", desc: "Equilibras opiniones puntuales negativas con un volumen sano." },
      { title: "Más visibilidad", desc: "Google premia a las fichas con actividad real de reseñas." },
      { title: "Más conversiones", desc: "Más estrellas se traduce en más llamadas y reservas." },
      { title: "Mejor SEO local", desc: "Las reseñas con texto aportan señales de relevancia local." },
      { title: "Reputación sólida", desc: "Una base amplia de reseñas amortigua opiniones puntuales malas." },
    ],
  },
  features: { title: "Funcionalidades para conseguir más reseñas", items: COMMON_FEATURES_FULL },
  faq: {
    items: [
      { q: "¿Compráis reseñas o generáis falsas?", a: "Nunca. Eso está prohibido por Google y daña tu negocio. Solo facilitamos pedir reseñas reales a clientes reales." },
      { q: "¿Cómo se piden las reseñas?", a: "Con mensajes profesionales y enlaces directos a tu ficha de Google, en el momento adecuado tras el servicio." },
      { q: "¿Sirve para cualquier sector?", a: "Sí, adaptamos el flujo y el tono a cada tipo de negocio." },
      { q: "¿Y si me llega una reseña negativa?", a: "Recibirás una alerta inmediata y te ayudamos a responder con criterio profesional." },
      { q: "¿Puedo automatizarlo?", a: "Sí, dentro del cumplimiento de normas de Google y siempre respetando al cliente." },
    ],
  },
  internalLinks: {
    title: "Sigue explorando",
    links: pickLinks([
      "mejorar-resenas-google",
      "responder-resenas-google",
      "resenas-negativas-google",
      "mejorar-posicionamiento-google-maps",
    ]),
  },
  ctaFinal: {
    title: "Más reseñas reales de tus clientes",
    subtitle: "Te enseñamos en una demo cómo organizar la solicitud de reseñas sin caer en malas prácticas.",
    cta: "Solicitar demo",
  },
};

const mejorarResenas: LandingData = {
  slug: "mejorar-resenas-google",
  seo: {
    title: "Mejorar reseñas de Google | LevelLocal",
    description:
      "Mejora tus reseñas de Google: sube tu valoración media, responde mejor a cada opinión y refuerza la confianza de tus futuros clientes.",
  },
  hero: {
    eyebrow: "Reseñas",
    h1: "Mejora tus reseñas de Google y genera más confianza",
    subtitle:
      "Trabajamos en tu valoración media, en la respuesta a cada reseña y en cómo perciben tu negocio quienes te están descubriendo en Google.",
    ctaPrimary: "Solicitar demo",
    ctaSecondary: "Ver planes",
  },
  problem: {
    title: "Tu valoración en Google es tu carta de presentación",
    paragraphs: [
      "Una valoración media baja, reseñas negativas sin responder o respuestas defensivas hacen que el cliente desconfíe antes incluso de hablar contigo.",
      "Mejorar tus reseñas no es solo conseguir más estrellas: es trabajar la percepción global del negocio.",
    ],
  },
  solution: {
    title: "Cómo te ayuda LevelLocal",
    paragraphs: [
      "LevelLocal te ayuda a subir tu valoración media solicitando reseñas reales, responder con criterio profesional a cada opinión y reaccionar a tiempo ante reseñas negativas.",
      "Trabajamos la reputación como un proceso continuo, no como un parche puntual.",
    ],
  },
  benefits: {
    title: "Lo que mejora cuando trabajas tus reseñas",
    items: [
      { title: "Valoración media", desc: "Más estrellas reales, sin trampas." },
      { title: "Percepción", desc: "El cliente ve un negocio que cuida a sus clientes." },
      { title: "Confianza", desc: "Más respuesta profesional, menos dudas." },
      { title: "Ratio de conversión", desc: "Más llamadas, formularios y reservas." },
      { title: "Reputación sostenida", desc: "No solo arreglas hoy, mantienes el nivel." },
      { title: "Crisis controladas", desc: "Reseñas negativas detectadas y respondidas a tiempo." },
    ],
  },
  features: { title: "Funcionalidades para mejorar tus reseñas", items: COMMON_FEATURES_FULL },
  faq: {
    items: [
      { q: "¿Se pueden borrar reseñas negativas?", a: "Solo se pueden reportar a Google si incumplen sus normas. Lo importante es responder bien y diluirlas con más reseñas reales positivas." },
      { q: "¿Cuánto puedo subir mi valoración media?", a: "Depende del punto de partida y del volumen de clientes. Es realista subir varias décimas en pocos meses con un buen plan." },
      { q: "¿Tengo que responder a todas las reseñas?", a: "Sí, idealmente. Responder a positivas y negativas transmite profesionalidad." },
      { q: "¿La IA mantiene mi tono de marca?", a: "Sí, configuramos el tono y vocabulario adaptados a tu marca." },
      { q: "¿Sirve si tengo varias ubicaciones?", a: "Sí, gestionamos reseñas de todas tus ubicaciones desde un único panel." },
    ],
  },
  internalLinks: {
    title: "Sigue explorando",
    links: pickLinks([
      "conseguir-resenas-google",
      "responder-resenas-google",
      "resenas-negativas-google",
      "mejorar-posicionamiento-google-maps",
    ]),
  },
  ctaFinal: {
    title: "Sube tu valoración en Google",
    subtitle: "Te enseñamos en una demo cómo mejorar tus reseñas de forma sostenida y sin malas prácticas.",
    cta: "Solicitar demo",
  },
};

const responderResenas: LandingData = {
  slug: "responder-resenas-google",
  seo: {
    title: "Responder reseñas de Google | LevelLocal",
    description:
      "Responde mejor a tus reseñas de Google con borradores generados por IA y revisión humana. Tono profesional adaptado a tu marca.",
  },
  hero: {
    eyebrow: "Respuestas",
    h1: "Responde mejor a tus reseñas de Google",
    subtitle:
      "Borradores con IA adaptados a tu marca y a cada tipo de reseña, con revisión humana antes de publicar. Sin respuestas robóticas.",
    ctaPrimary: "Solicitar demo",
    ctaSecondary: "Ver planes",
  },
  problem: {
    title: "Las reseñas sin respuesta son una oportunidad perdida",
    paragraphs: [
      "Cada reseña que no respondes es una conversación que no estás teniendo con tu cliente y con quienes están leyendo esa reseña antes de decidir.",
      "Y respuestas con plantilla genérica son casi peor: transmiten desinterés y dañan la marca.",
    ],
  },
  solution: {
    title: "Cómo te ayuda LevelLocal",
    paragraphs: [
      "Generamos un borrador de respuesta personalizado para cada reseña, basado en su contenido, en el tono de tu marca y en si es positiva, negativa o neutra.",
      "Tu equipo lo revisa, edita si quiere y publica. Mantienes el control y ahorras horas de trabajo cada semana.",
    ],
  },
  benefits: {
    title: "Por qué responder reseñas mejora tu negocio",
    items: [
      { title: "Más confianza", desc: "Las respuestas demuestran que cuidas a tus clientes." },
      { title: "Crisis controladas", desc: "Una buena respuesta neutraliza una reseña negativa." },
      { title: "Mejor SEO local", desc: "Google valora la actividad de respuestas en la ficha." },
      { title: "Tono coherente", desc: "Toda tu marca habla con la misma voz." },
      { title: "Ahorro de tiempo", desc: "Borradores listos en segundos." },
      { title: "Control humano", desc: "Nada se publica sin tu visto bueno." },
    ],
  },
  features: { title: "Funcionalidades para responder reseñas", items: COMMON_FEATURES_FULL },
  faq: {
    items: [
      { q: "¿La IA publica respuestas sin revisión?", a: "No por defecto. Generamos borradores y tu equipo aprueba antes de publicar. Solo si así lo decides puedes activar publicación automática para reseñas positivas." },
      { q: "¿Puedo personalizar el tono?", a: "Sí, configuramos el tono y vocabulario para que sea coherente con tu marca." },
      { q: "¿Sirve para reseñas negativas?", a: "Especialmente. La IA propone una respuesta empática y profesional que tu equipo valida." },
      { q: "¿En qué idiomas funciona?", a: "Por defecto en español. Bajo proyecto, también en inglés y francés." },
      { q: "¿Funciona con varias ubicaciones?", a: "Sí, con permisos por ubicación y panel centralizado." },
    ],
  },
  internalLinks: {
    title: "Sigue explorando",
    links: pickLinks([
      "mejorar-resenas-google",
      "resenas-negativas-google",
      "conseguir-resenas-google",
      "mejorar-posicionamiento-google-maps",
    ]),
  },
  ctaFinal: {
    title: "Responde mejor a cada reseña de Google",
    subtitle: "Te enseñamos en una demo cómo automatizar borradores sin perder control humano.",
    cta: "Solicitar demo",
  },
};

const resenasNegativas: LandingData = {
  slug: "resenas-negativas-google",
  seo: {
    title: "Reseñas negativas en Google: cómo gestionarlas | LevelLocal",
    description:
      "Gestiona reseñas negativas en Google con alertas inmediatas, respuestas profesionales y un protocolo de gestión de crisis.",
  },
  hero: {
    eyebrow: "Reputación",
    h1: "Gestiona reseñas negativas antes de que dañen tu reputación",
    subtitle:
      "Alertas inmediatas, borradores de respuesta profesional y protocolo de crisis para que ninguna reseña negativa te pille desprevenido.",
    ctaPrimary: "Solicitar demo",
    ctaSecondary: "Ver planes",
  },
  problem: {
    title: "Una reseña negativa mal gestionada se queda años",
    paragraphs: [
      "Una reseña negativa visible y sin respuesta puede frenar reservas durante meses, e incluso convertirse en la primera impresión de cualquiera que te busque en Google.",
      "Y peor aún: una respuesta defensiva, agresiva o que parezca culpar al cliente puede empeorar la situación y volverse viral.",
    ],
  },
  solution: {
    title: "Cómo te ayuda LevelLocal",
    paragraphs: [
      "Recibes una alerta inmediata cuando llega una reseña baja, con un borrador de respuesta profesional adaptado al caso y a tu marca.",
      "Tu equipo decide cómo responder con criterio y a tiempo, antes de que la reseña impacte en tu reputación.",
    ],
  },
  benefits: {
    title: "Lo que ganas gestionando bien las reseñas negativas",
    items: [
      { title: "Tiempo de reacción", desc: "Alertas en cuanto llega la reseña, no días después." },
      { title: "Respuestas profesionales", desc: "Sin defensiveness, sin culpar al cliente." },
      { title: "Reputación protegida", desc: "Evitas crisis innecesarias." },
      { title: "Aprendizaje", desc: "Detectas problemas reales del servicio." },
      { title: "Confianza recuperada", desc: "Una buena respuesta convence a quien lee la reseña." },
      { title: "Control", desc: "Aprobación humana obligatoria si lo necesitas." },
    ],
  },
  features: { title: "Funcionalidades para gestionar reseñas negativas", items: COMMON_FEATURES_FULL },
  faq: {
    items: [
      { q: "¿Se pueden eliminar reseñas negativas?", a: "Solo si incumplen las normas de Google. Si son legítimas, lo correcto es responder bien y trabajar para diluirlas con más reseñas reales positivas." },
      { q: "¿Cómo de rápido recibo la alerta?", a: "En cuanto la reseña aparece en tu ficha. Puedes recibirla por email." },
      { q: "¿Qué tono usan las respuestas?", a: "Empático, profesional y orientado a solución. Sin culpar al cliente." },
      { q: "¿Puedo revisar antes de publicar?", a: "Sí, siempre. La aprobación humana puede ser obligatoria." },
      { q: "¿Sirve para varias ubicaciones?", a: "Sí, con alertas y permisos por ubicación." },
    ],
  },
  internalLinks: {
    title: "Sigue explorando",
    links: pickLinks([
      "responder-resenas-google",
      "mejorar-resenas-google",
      "conseguir-resenas-google",
      "mejorar-posicionamiento-google-maps",
    ]),
  },
  ctaFinal: {
    title: "Protege tu reputación en Google",
    subtitle: "Te enseñamos en una demo cómo activar alertas y protocolo de respuesta para reseñas negativas.",
    cta: "Solicitar demo",
  },
};

const seoLocal: LandingData = {
  slug: "seo-local-google-maps",
  seo: {
    title: "SEO local en Google Maps | LevelLocal",
    description:
      "SEO local para negocios que quieren destacar en Google Maps: trabaja reseñas, ficha, contenido, respuestas y métricas desde una única plataforma.",
  },
  hero: {
    eyebrow: "SEO local",
    h1: "SEO local para negocios que quieren destacar en Google Maps",
    subtitle:
      "Trabaja todos los factores que Google valora para mostrar tu negocio en búsquedas locales: reseñas, actividad, contenido, fotos, horarios y respuestas.",
    ctaPrimary: "Solicitar demo",
    ctaSecondary: "Ver planes",
  },
  problem: {
    title: "El SEO local va más allá de aparecer en Google",
    paragraphs: [
      "Aparecer entre los primeros resultados locales depende de muchos factores: la antigüedad y completitud de tu ficha, las reseñas, las respuestas, la actividad reciente y la coherencia de la información.",
      "Centrarse solo en una palabra clave o en una etiqueta no funciona: el SEO local es un trabajo continuo de muchos pequeños frentes.",
    ],
  },
  solution: {
    title: "Cómo te ayuda LevelLocal",
    paragraphs: [
      "LevelLocal te permite trabajar en paralelo todos los factores que el algoritmo local de Google sí valora, sin tener que dedicarle horas todas las semanas.",
      "Una plataforma única para reseñas, respuestas, publicaciones, fotos, horarios, alertas e informes.",
    ],
  },
  benefits: {
    title: "Factores de SEO local que te ayudamos a trabajar",
    items: [
      { title: "Reseñas reales", desc: "Volumen y calidad de opiniones." },
      { title: "Actividad", desc: "Publicaciones recurrentes en la ficha." },
      { title: "Información completa", desc: "Servicios, horarios, fotos y categorías." },
      { title: "Respuestas", desc: "Atención profesional a cada reseña." },
      { title: "Coherencia", desc: "Misma información en Google y en tu web." },
      { title: "Métricas", desc: "Llamadas, indicaciones y visitas web." },
    ],
  },
  features: { title: "Plataforma de SEO local todo-en-uno", items: COMMON_FEATURES_FULL },
  faq: {
    items: [
      { q: "¿Qué es el SEO local?", a: "Es el conjunto de técnicas para mejorar la visibilidad de un negocio en búsquedas con intención local: \"cerca de mí\", nombre del barrio o de la ciudad, etc." },
      { q: "¿Es lo mismo que SEO tradicional?", a: "No. El SEO local depende sobre todo de tu ficha de Google Maps, tus reseñas y la coherencia de tus datos. El SEO tradicional depende de tu web." },
      { q: "¿Tengo que tener web para hacer SEO local?", a: "Es muy recomendable, pero gran parte del SEO local se trabaja desde la propia ficha de Google." },
      { q: "¿En cuánto tiempo veré resultados?", a: "Las primeras mejoras se notan en semanas; los resultados sostenidos se consolidan en pocos meses." },
      { q: "¿Sirve para cualquier sector?", a: "Sí, especialmente para negocios con clientes locales." },
    ],
  },
  internalLinks: {
    title: "Sigue explorando",
    links: pickLinks([
      "mejorar-posicionamiento-google-maps",
      "conseguir-resenas-google",
      "mejorar-resenas-google",
      "varias-ubicaciones-google-maps",
    ]),
  },
  ctaFinal: {
    title: "SEO local sin complicaciones técnicas",
    subtitle: "Te enseñamos en una demo cómo trabajar tu SEO local desde un único panel.",
    cta: "Solicitar demo",
  },
};

/* ============================================================
   4. AGENCIAS Y MULTI-UBICACIÓN
   ============================================================ */

const agencias: LandingData = {
  slug: "agencias-seo-local",
  seo: {
    title: "Herramienta SEO local para agencias | LevelLocal",
    description:
      "Plataforma white-label de SEO local y reseñas Google para agencias. Servicio recurrente, comisión por cliente y panel multi-cuenta.",
  },
  hero: {
    eyebrow: "Agencias",
    h1: "Plataforma white-label de SEO local y reseñas para agencias",
    subtitle:
      "Añade un servicio recurrente de gestión de reseñas Google y posicionamiento local a tu cartera. White-label disponible, comisión por cliente activo y panel multi-cuenta para gestionar decenas de fichas desde un único lugar.",
    ctaPrimary: "Solicitar propuesta para agencias",
    ctaSecondary: "Ver planes",
  },
  problem: {
    title: "Tus clientes te piden gestión de reseñas y SEO local",
    paragraphs: [
      "Cada vez más clientes locales preguntan a sus agencias por reseñas, reputación online y posicionamiento en Google Maps. Gestionarlo manualmente es ineficiente y poco rentable.",
      "Construir una herramienta propia es caro, lento y complejo. Reusar varias herramientas sueltas se vuelve un problema operativo.",
    ],
  },
  solution: {
    title: "Cómo te ayuda LevelLocal",
    paragraphs: [
      "Con LevelLocal aportas a tus clientes una plataforma profesional de gestión de reseñas, reputación y SEO local en Google Maps, sin desarrollar tecnología propia.",
      "Tú añades un servicio recurrente y rentable a tu propuesta, y tu cliente final mejora su presencia en Google Maps.",
    ],
  },
  benefits: {
    title: "Por qué las agencias eligen LevelLocal",
    items: [
      { title: "Comisión recurrente", desc: "Cobras un % cada mes por cada cliente activo que gestionas." },
      { title: "White-label disponible", desc: "Plataforma con tu marca, tu logo y tu dominio según el volumen." },
      { title: "Panel multi-cuenta", desc: "Gestiona decenas de fichas y clientes desde un único acceso." },
      { title: "Informes con tu marca", desc: "Reportes mensuales descargables y enviables a tu cliente final." },
      { title: "Borradores con IA", desc: "Respondes reseñas en segundos con tono de marca por cliente." },
      { title: "Onboarding asistido", desc: "Te ayudamos a migrar y activar a tus clientes en español." },
    ],
  },
  features: { title: "Funcionalidades pensadas para agencias", items: COMMON_FEATURES_FULL },
  faq: {
    items: [
      { q: "¿Qué modelo comercial tengo como agencia?", a: "Hay un modelo de partner por volumen de ubicaciones gestionadas. Te enviamos propuesta a medida en función de tu cartera." },
      { q: "¿Puedo white-label la plataforma?", a: "Hablamos contigo de las opciones de marca según el tamaño de la cartera." },
      { q: "¿Funciona si mis clientes tienen 1 o 100 ubicaciones?", a: "Sí, está pensado tanto para clientes con una sola ficha como para cadenas y franquicias." },
      { q: "¿Quién responde a las reseñas, vosotros o la agencia?", a: "Lo decides tú. La agencia puede operar la plataforma o el cliente final, con permisos por usuario y por ubicación." },
      { q: "¿Cómo es el onboarding?", a: "Te acompañamos en la activación, formación y migración de tus clientes." },
    ],
  },
  internalLinks: {
    title: "Sigue explorando",
    links: pickLinks([
      "varias-ubicaciones-google-maps",
      "mejorar-posicionamiento-google-maps",
      "conseguir-resenas-google",
      "responder-resenas-google",
    ]),
  },
  ctaFinal: {
    title: "Convierte SEO local en un servicio recurrente",
    subtitle: "Te enviamos una propuesta a medida para tu agencia y un onboarding asistido.",
    cta: "Solicitar propuesta para agencias",
  },
};

const variasUbicaciones: LandingData = {
  slug: "varias-ubicaciones-google-maps",
  seo: {
    title: "Gestionar varias fichas de Google Maps en un panel | LevelLocal",
    description:
      "Compara el rendimiento de cada sede, detecta las que pierden clientes y responde reseñas por ubicación sin perder el control de la marca.",
  },
  hero: {
    eyebrow: "Multi-ubicación",
    h1: "Gestiona varias fichas de Google Maps desde un único panel",
    subtitle:
      "Compara el rendimiento de cada sede, detecta las ubicaciones que pierden clientes y responde reseñas por ubicación sin perder el control de la marca. Pensado para franquicias, cadenas y grupos con varias sedes.",
    ctaPrimary: "Solicitar demo para varias ubicaciones",
    ctaSecondary: "Ver planes",
  },
  problem: {
    title: "Cuando tienes varias ubicaciones, todo se multiplica",
    paragraphs: [
      "Cada ubicación tiene su propia ficha, sus propias reseñas, sus propias respuestas y sus propias métricas. Gestionarlo manualmente es ineficiente y descoordinado.",
      "Y peor aún: una sola sede con mala reputación arrastra la percepción de toda la marca.",
    ],
  },
  solution: {
    title: "Cómo te ayuda LevelLocal",
    paragraphs: [
      "Centralizas todas tus ubicaciones en un único panel: reseñas, respuestas, alertas, publicaciones, métricas e informes comparativos.",
      "Cada sede mantiene su autonomía operativa, pero la marca conserva el control y la coherencia.",
    ],
  },
  benefits: {
    title: "Por qué las cadenas eligen LevelLocal",
    items: [
      { title: "Control centralizado", desc: "Toda la operación reputacional en un único panel." },
      { title: "Comparativa entre sedes", desc: "Detecta qué ubicación rinde mejor y cuál necesita ayuda." },
      { title: "Alertas por sede", desc: "Reseñas negativas detectadas al instante." },
      { title: "Respuestas homogéneas", desc: "Mismo tono de marca en todas las ubicaciones." },
      { title: "Informes ejecutivos", desc: "Vista para dirección, vista operativa para responsables." },
      { title: "Permisos por usuario", desc: "Cada sede ve solo lo suyo." },
    ],
  },
  features: { title: "Funcionalidades para multi-ubicación", items: COMMON_FEATURES_FULL },
  faq: {
    items: [
      { q: "¿Cuántas ubicaciones puedo gestionar?", a: "Desde unas pocas hasta cientos. Adaptamos el plan al volumen de fichas." },
      { q: "¿Cada ubicación tiene su propio acceso?", a: "Sí, permisos por usuario y por ubicación." },
      { q: "¿Puedo unificar el tono de respuesta entre sedes?", a: "Sí, definimos un tono de marca y la IA lo respeta en todas las sedes." },
      { q: "¿Hay informes consolidados?", a: "Sí, informes ejecutivos por marca y operativos por sede." },
      { q: "¿Sirve para franquicias?", a: "Sí, especialmente para franquicias y cadenas con varias marcas." },
    ],
  },
  internalLinks: {
    title: "Sigue explorando",
    links: pickLinks([
      "agencias-seo-local",
      "mejorar-posicionamiento-google-maps",
      "responder-resenas-google",
      "resenas-negativas-google",
    ]),
  },
  ctaFinal: {
    title: "Una sola plataforma para todas tus ubicaciones",
    subtitle: "Te enseñamos en una demo cómo controlar la reputación de toda tu red desde un único panel.",
    cta: "Solicitar demo para varias ubicaciones",
  },
};

/* ============================================================
   EXPORT
   ============================================================ */

export const LANDINGS: LandingData[] = [
  // Sectoriales
  restaurantes,
  clinicas,
  hoteles,
  inmobiliarias,
  talleres,
  estetica,
  lujo,
  // Ciudades
  malaga,
  sevilla,
  cadiz,
  valladolid,
  granada,
  // Apoyo SEO general → movido a /blog/<slug>
  // Agencias y multi
  agencias,
  variasUbicaciones,
];

export const LANDINGS_BY_SLUG: Record<string, LandingData> = Object.fromEntries(
  LANDINGS.map((l) => [l.slug, l]),
);